import React from 'react'
import PropTypes from 'prop-types'
import { Icon, Header, ColorSelector, ColorCircle, MultiSelector, DatePicker, List } from '../index'
import './feature2.css'

/**
 * Feature
 */
class Feature extends React.Component {

  componentDidMount() {
    if (this.props.default && this.props.onChange)
      this.props.onChange(this.props.id, this.props.default)
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.filterValue !== null && nextProps.filterValue !== this.props.filterValue) {
      this.setState({ mode: 'EDIT', value: '' })
    }
  }

  state = {
    mode: this.props.mode,
    value: this.props.default,
    customized: false
  }

  toggleMode = () => {
    const mode = this.state.mode != 'INFO' ? 'INFO' : this.state.customized ? 'CUSTOM' : 'EDIT'
    this.setState({ mode })
  }

  toggleCustom = () => {
    this.setState({ mode: 'CUSTOM' })
  }

  onClear = () => {
    const mode = this.state.customized ? 'EDIT' : 'INFO'
    this.setState({ value: '', mode, customized: false })
    if (this.props.onChange) this.props.onChange(this.props.id, '')
  }

  onChange = (value, customized = false) => {
    if (!this.props.notoggle) this.setState({ value, mode: 'INFO', customized })
    if (this.props.onChange) this.props.onChange(this.props.id, value, customized)
  }

  render() {
    const { excludeBy, excludeValues, exclusion } = this.props
    const excluded = excludeBy && excludeValues && exclusion ? excludeValues.some(value => value === exclusion || value === '*') : false
    return excluded ? '' : <FeatureBox {...this.props} {...this.state} toggleModel={this.toggleMode} toggleCustom={this.toggleCustom} onClear={this.onClear} onChange={this.onChange} />
  }

}

Feature.defaultProps = {
  mode: 'INFO'
}

Feature.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  icon: PropTypes.string,
  label: PropTypes.string,
  default: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  options: PropTypes.array,
  custom: PropTypes.bool,
  onChange: PropTypes.func,
}

/**
 * FeatureBox
 */
const FeatureBox = (props) => {

  const ToggleIcon = ({ open = false }) => {
    const iconName = !open ? 'keyboard_arrow_left' : 'keyboard_arrow_right'
    const stateClass = open ? 'open' : 'closed'
    return props.notoggle ? '' : <Icon className={`feature-toggle-icon ${stateClass}`} icon={iconName} clickable action={props.toggleModel} />
  }

  const render = () => {
    if (props.type === 'BOOLEAN') {
      return <Switch {...props} />
    } else {
      switch (props.mode) {
        case 'READONLY': return [<FeatureInfo value={props.value} readOnly={true} />]
        case 'INFO': return [<ToggleIcon open={false} />, <FeatureInfo   {...props} />]
        case 'EDIT': return [<ToggleIcon open={true} />, <FeatureEdit   {...props} />]
        case 'CUSTOM': return [<ToggleIcon open={true} />, <FeatureCustom {...props} />]
      }
    }
  }

  const icon = {
    icon: props.icon,
    src: props.src,
    decorator: props.customized ? 'edit' : props.mandatory ? props.value ? 'done' : 'close' : '',
    className: props.customized ? 'custom' : props.mandatory ? props.value ? 'ok' : 'error' : '',
  }

  return (
    <div className={`feature ${props.className}`} >
      <Header icon={icon} title={props.label}>
        {render()}
      </Header>
      {props.max ? <span className="feature-max">max {props.max}</span> : ''}
      {props.description ? <span className="feature-description">{props.description}</span> : ''}
    </div>
  )
}

/**
 * Clear icon
 */
const ClearIcon = ({ onClear }) => {
  return <Icon icon="close" clickable action={onClear} className="small square" />
  return (
    <span className={`icon clickable  small square`} onMouseDown={onClear}>
      <span className="material-icons">close</span>
    </span>
  )
}

/**
 * Toggle Custom icon
 */
const CustomIcon = ({ enabled = false, toggleCustom }) => {
  return enabled ? <Icon icon="edit" clickable action={toggleCustom} className="small square" /> : ''
}

/**
 * Feature Info
 */
const FeatureInfo = ({ type, value, options = [], customized, readOnly=false, toggleModel, onChange }) => {
  let info = value
  if (type === 'SELECTION' || type === 'ADDRESS') {
    const option = options.find(option => option.value === value)
    if (option) info = option.label
  }
  if (type === 'DATE' && value) {
    const { year, month, day } = value
    info = `${day}/${month}/${year}`
  }
  if (type === 'COLORS') {
    info = Array.isArray(value) ? value : []
  }
  return type === 'COLORS' ? <ColorSelector colors={info} /> : <span className="feature-info clickable" onClick={() => { if (!readOnly) toggleModel() }} tabIndex="0"> {info} </span>
}

/**
 * Feature Edit
 */
const FeatureEdit = (props) => {
  const clearIcon = props.mandatory || props.noclear ? '' : <ClearIcon onClear={props.onClear} />
  const colors = props.type === 'COLORS' && Array.isArray(props.value) ? props.value : []
  
  switch (props.type) {
    case "NUMBER": return [clearIcon, <TextInput {...props} input="number" />]
    case "STRING": return [clearIcon, <TextInput {...props} />]
    case "BOOLEAN": return [<Switch {...props} />]
    case "SELECTION": return [clearIcon, <CustomIcon enabled={props.custom} toggleCustom={props.toggleCustom} />, <Selector {...props} />]
    case "COLORS": return [<ColorSelector {...props} colors={colors} editable={true}/>]
    case "DATE": return [clearIcon, <dialog><DatePicker {...props} /></dialog>]
    case "ADDRESS": return [clearIcon, <TextInput {...props} />]
    default: return [<div> No editor for type {props.type}</div>]
  }

}

/**
 * Feature Custom
 */
const FeatureCustom = ({ value, onChange, onClear }) => {
  const handleChange = (value) => {
    if (onChange) onChange(value, true)
  }
  return [
    <ClearIcon onClear={onClear} />,
    <TextInput value={value} onChange={handleChange} />
  ]
}

/**
 * Selection Lane
 */
const Selector = ({ options, filterValue, onChange }) => {

  const renderOptions = () => {
    return options
      .filter(option => filterValue ? option.filter === filterValue : true)
      .map(option => {
        const handleClick = () => { if (onChange) onChange(option.value) }
        return <div className="feature-selector lane-item" onClick={handleClick}>{option.label}</div>
      })
  }

  return (
    <div className="lane">
      {renderOptions()}
    </div>
  )
}

/**
 * Color Selection Lane
 */
const _ColorSelector = ({ options, filterValue, onChange }) => {
  return (
    <div className="lane">
      <MultiSelector options={options} onChange={onChange} className="color" />
    </div>
  )
}

export default Feature;

/**
 * TextInput
 */
const TextInput = class extends React.Component{ 

  constructor(props) {
    super(props)
    this.field = null
    this.setField = element => {
      this.field = element
      if (element) element.focus()
    }
  }

  render() {

    const { value = '', onChange, input = 'text', max = 99999999 } = this.props

    const onKeyUp = (event) => {
      if (max && this.field && this.field.value > max) {
        this.field.value = this.field.value.slice(0, this.field.value.length - 1);
      }
      if (event.keyCode === 13) {
        if (typeof onChange === "function") onChange(this.field.value)
      }
    }

    const onBlur = (ev) => {
      if (this.field) {
        //console.log(value, '>>',this.field.value, 'change', this.field.value != value && this.field.value !== '')
        if (this.field.value != value && this.field.value != '') {
          if (typeof onChange === "function") onChange(this.field.value)
        } else {
          if (typeof onChange === "function") onChange(value)
        }
      }
    }

    const style = input === 'text' ? 'feature-text' : 'feature-number'

    return (
      <div className={`${style}`}>
        <input type={input} max={max} placeholder={value} onKeyUp={onKeyUp} autoFocus={true}
          ref={this.setField}
          onBlur={() => {
            if (this.field) setTimeout(onBlur, 10)
          }}
        />
      </div>
    )
  }
}

/**
 * SwitchInput
 */
const Switch = ({ id, value = false, onChange }) => {

  const toggle = () => {
    if (onChange) onChange(!value);
  }

  return (
    <label className="switch2">
      <input id={id} type="checkbox" checked={value} onChange={toggle} />
      <span className="slider round" />
    </label>
  )
}



// WEBPACK FOOTER //
// ./src/lib/components/feature2.js